import React, { useEffect, useRef, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { IconButton, TextField } from '@mui/material';
import { PrimaryButton } from 'v3/Components/Shared/Buttons';
import Tag from 'v3/Components/Shared/Tag';
import cloudyIcon from 'Assets/Icons/cloudy.svg';
import { ReviewerNote } from 'v3/Models/ReviewerNotes';
import { TYPE_TAG_COLOR, formatNoteTimestamp, getNoteTagLabel } from './ReviewerProfile.NoteDetail';
import './ReviewerProfile.NoteList.scss';

export type NoteTypeFilterOption = {
  value: number;
  displayName: string;
};

type Props = {
  notes: ReviewerNote[];
  isLoading: boolean;
  selectedNoteId?: string;
  onSelectNote: (id: string) => void;
  onAddNote: () => void;
  canAddNote: boolean;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  typeFilterOptions: NoteTypeFilterOption[];
  activeTypeValues: number[];
  systemActive: boolean;
  onToggleType: (value: number) => void;
  onToggleSystem: () => void;
  onApplyQuickFilters: () => void;
};

const NoteListSkeleton: React.FC = () => (
  <div className="note-list__skeleton">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="note-list__skeleton-item">
        <div className="note-list__skeleton-bone note-list__skeleton-bone--title" />
        <div className="note-list__skeleton-bone note-list__skeleton-bone--line" />
        <div className="note-list__skeleton-bone note-list__skeleton-bone--line-short" />
        <div className="note-list__skeleton-bone note-list__skeleton-bone--meta" />
      </div>
    ))}
  </div>
);

const NoteListEmptyState: React.FC<{ canAddNote: boolean; onAddNote: () => void }> = ({
  canAddNote,
  onAddNote,
}) => (
  <div className="note-list__empty">
    <img src={cloudyIcon} alt="" aria-hidden="true" className="note-list__mascot" />
    <div className="note-list__empty-text">
      <span className="note-list__empty-heading">No notes yet</span>
      <span className="note-list__empty-subtext">ADD A NOTE TO GET STARTED</span>
    </div>
    {canAddNote && (
      <PrimaryButton className="centered" title="Add Note" onClick={onAddNote} />
    )}
  </div>
);

const NoteListNoResults: React.FC<{ searchTerm: string }> = ({ searchTerm }) => (
  <div className="note-list__empty">
    <img src={cloudyIcon} alt="" aria-hidden="true" className="note-list__mascot" />
    <div className="note-list__empty-text">
      <span className="note-list__empty-heading">No notes found</span>
      <span className="note-list__empty-subtext">
        {searchTerm.trim()
          ? `NO RESULTS FOR "${searchTerm.trim()}"`
          : 'NO NOTES MATCH THE SELECTED FILTERS'}
      </span>
    </div>
  </div>
);

const NoteListNoFiltersActive: React.FC<{ onApplyQuickFilters: () => void }> = ({ onApplyQuickFilters }) => (
  <div className="note-list__empty">
    <img src={cloudyIcon} alt="" aria-hidden="true" className="note-list__mascot" />
    <div className="note-list__empty-text">
      <span className="note-list__empty-heading">No notes found</span>
      <span className="note-list__empty-subtext">ALL QUICK FILTERS ARE OFF</span>
    </div>
    <PrimaryButton className="centered" title="Apply quick filters to see notes" onClick={onApplyQuickFilters} />
  </div>
);

const NoteListSearch: React.FC<{ value: string; onChange: (value: string) => void }> = ({ value, onChange }) => {
  const [manuallyOpened, setManuallyOpened] = useState(false);
  const open = manuallyOpened || value.length > 0;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    function onGlobalKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && open) {
        onChange('');
        setManuallyOpened(false);
      }
    }
    window.addEventListener('keydown', onGlobalKey);
    return () => window.removeEventListener('keydown', onGlobalKey);
  }, [open, onChange]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setManuallyOpened(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  function onIconClick() {
    if (open) {
      onChange('');
      setManuallyOpened(false);
    } else {
      setManuallyOpened(true);
    }
  }

  return (
    <div className={`note-list__search ${open ? 'note-list__search--open' : ''}`} ref={containerRef}>
      <div className="note-list__search-input-wrap">
        <TextField
          inputRef={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="note-list__search-input"
          variant="standard"
          placeholder="Search notes"
          onBlur={() => {
            if (!value) setManuallyOpened(false);
          }}
          InputProps={{ disableUnderline: true }}
        />
      </div>
      <IconButton
        aria-label={open ? 'Close search' : 'Search notes'}
        onClick={onIconClick}
        onMouseDown={(e) => e.preventDefault()}
        className="note-list__search-iconbutton"
      >
        <SearchIcon />
      </IconButton>
    </div>
  );
};

const NoteFilterChip: React.FC<{
  label: string;
  active: boolean;
  onToggle: () => void;
}> = ({ label, active, onToggle }) => (
  <button
    type="button"
    className={`note-list__filter-chip ${active ? 'note-list__filter-chip--active' : 'note-list__filter-chip--inactive'}`}
    aria-pressed={active}
    onClick={onToggle}
  >
    <span className="note-list__filter-chip-label">{label}</span>
    {active && (
      <span className="note-list__filter-chip-remove">
        <CloseIcon />
      </span>
    )}
  </button>
);

const NoteListFilters: React.FC<{
  typeFilterOptions: NoteTypeFilterOption[];
  activeTypeValues: number[];
  systemActive: boolean;
  onToggleType: (value: number) => void;
  onToggleSystem: () => void;
}> = ({ typeFilterOptions, activeTypeValues, systemActive, onToggleType, onToggleSystem }) => (
  <div className="note-list__filters">
    {typeFilterOptions.map((option) => (
      <NoteFilterChip
        key={option.value}
        label={option.displayName}
        active={activeTypeValues.includes(option.value)}
        onToggle={() => onToggleType(option.value)}
      />
    ))}
    <NoteFilterChip label="System" active={systemActive} onToggle={onToggleSystem} />
  </div>
);

const NoteListItem: React.FC<{
  note: ReviewerNote;
  selected: boolean;
  onClick: () => void;
}> = ({ note, selected, onClick }) => {
  const tagLabel = getNoteTagLabel(note);
  const tagColor = TYPE_TAG_COLOR[tagLabel] ?? 'grey';

  return (
    <button
      type="button"
      className={`note-list__item ${selected ? 'note-list__item--selected' : ''}`}
      onClick={onClick}
    >
      <div className="note-list__item-row note-list__item-row--header">
        <div className="note-list__item-heading">
          <span className="note-list__item-title">{note.title}</span>
          <span className="note-list__item-meta">
            {formatNoteTimestamp(note.dateCreated)} | {note.createdBy}
          </span>
        </div>
        {tagLabel && <Tag label={tagLabel} color={tagColor} />}
      </div>
      <div className="note-list__item-row note-list__item-row--body">
        <span className="note-list__item-snippet">{note.comment}</span>
        <span className="note-list__item-comment-count">
          <ChatBubbleOutlineIcon />
          {note.commentCount}
        </span>
      </div>
    </button>
  );
};

export const NoteList: React.FC<Props> = ({
  notes,
  isLoading,
  selectedNoteId,
  onSelectNote,
  onAddNote,
  canAddNote,
  searchTerm,
  onSearchChange,
  typeFilterOptions,
  activeTypeValues,
  systemActive,
  onToggleType,
  onToggleSystem,
  onApplyQuickFilters,
}) => {
  const hasSearchTerm = searchTerm.trim().length > 0;
  const hasActiveFilter = activeTypeValues.length < typeFilterOptions.length || !systemActive;
  const isNarrowed = hasSearchTerm || hasActiveFilter;
  const allFiltersOff = activeTypeValues.length === 0 && !systemActive;
  const showList = !isLoading && notes.length > 0;
  const showNoFiltersActive = !isLoading && notes.length === 0 && allFiltersOff;
  const showNoResults = !isLoading && notes.length === 0 && isNarrowed && !allFiltersOff;
  const [hasEverHadNotes, setHasEverHadNotes] = useState(notes.length > 0);
  useEffect(() => {
    if (notes.length > 0) setHasEverHadNotes(true);
  }, [notes.length]);
  const showEmptyState = !isLoading && notes.length === 0 && !isNarrowed && !hasEverHadNotes;
  const showHeaderActions = hasEverHadNotes || isNarrowed || notes.length > 0;

  return (
    <div className="note-list">
      <div className="note-list__header">
        <h3 className="note-list__title">
          {/* The empty state header reads just "Notes", with no count (TC-152). */}
          Notes{!isLoading && !showEmptyState && ` (${notes.length})`}
        </h3>
        {showHeaderActions && (
          <div className="note-list__header-actions">
            <NoteListSearch value={searchTerm} onChange={onSearchChange} />
            {canAddNote && (
              <PrimaryButton
                title="Add Note"
                className="centered"
                onClick={onAddNote}
                style={{ width: 'auto', padding: '0 20px', flexShrink: 0 }}
              />
            )}
          </div>
        )}
      </div>
      {showHeaderActions && (
        <NoteListFilters
          typeFilterOptions={typeFilterOptions}
          activeTypeValues={activeTypeValues}
          systemActive={systemActive}
          onToggleType={onToggleType}
          onToggleSystem={onToggleSystem}
        />
      )}
      <div className={`note-list__content ${showList ? 'note-list__content--list' : ''}`}>
        {isLoading && <NoteListSkeleton />}
        {showEmptyState && <NoteListEmptyState canAddNote={canAddNote} onAddNote={onAddNote} />}
        {showNoFiltersActive && <NoteListNoFiltersActive onApplyQuickFilters={onApplyQuickFilters} />}
        {showNoResults && <NoteListNoResults searchTerm={searchTerm} />}
        {showList &&
          notes.map((note) => (
            <NoteListItem
              key={note.id}
              note={note}
              selected={note.id === selectedNoteId}
              onClick={() => onSelectNote(note.id)}
            />
          ))}
      </div>
    </div>
  );
};

export default NoteList;
